import { type MutableRefObject } from "react";
import { type AbortController } from "@azure/abort-controller";

import TaskActions from "../../../../enums/TaskActions";
import DataService from "../../../Application/services/dataServices/typedDataService";
import backgroundTasksEventsEmitter from "../../../BackgroundTasks/events/backgroundTasksEventsEmitter";
import scormTasks from "../../../BackgroundTasks/scormTasks";

import { type FileLike } from "../../../../interfaces";
import { type AcceptCancelationHandler, type OnUploadProgressHandler } from "../../../../utils/fileUtils";
import { type ScormTask } from "../../../BackgroundTasks/taskPool";
import { type ScormFileRequest } from "./types";
import { type AllocatedResource, type SasToken } from "../../../../interfaces/AllocatedResource";

export interface UploaderServiceParams {
  canCancel: boolean;
  createAction: (data: ScormFileRequest) => Promise<void> | void;
  updateFileAction: (data: ScormFileRequest) => Promise<void> | void;
  uploadAction: (
    url: string,
    file: File,
    onUploadProgressHandler: OnUploadProgressHandler,
    acceptCancellationHandler: AcceptCancelationHandler,
  ) => Promise<void>;
  uploadCancelAction: () => void;
  idProvider: MutableRefObject<number>;
  onTaskIdChanged: (taskId: number) => void;
  tasksActions: {
    addTask: (task: ScormTask) => void;
    updateTask: (id: number, task: ScormTask) => void;
    deleteTask: (id: number | string) => void;
    updateProgress: (id: number, percent: number, indeterminate: boolean) => void;
    updateId: (oldId: number | string, newId: number) => void;
  };
}

const uploaderService = (params: UploaderServiceParams) => {
  let aborter: AbortController;

  const uploadFileHandler = async (oldTaskId: string, file: FileLike) => {
    const exists = params.idProvider.current > 0;

    const getResource = async (): Promise<AllocatedResource> => {
      const id = params.idProvider.current;
      return exists
        ? { sasToken: (await DataService.scorm.generateSas(id)).data, id }
        : (await DataService.scorm.allocateResources()).data;
    };

    const uploadFile = async (taskId: number, sasToken: SasToken) => {
      const onCancel = params.canCancel ? () => params.tasksActions.deleteTask(taskId) : undefined;
      params.tasksActions.updateTask(taskId, scormTasks.getStarted(taskId, file.name, onCancel));

      const acceptUploadAborter = (aborterHandler: AbortController) => {
        aborter = aborterHandler;
        backgroundTasksEventsEmitter.updateHandlerOnce(TaskActions.cancel, taskId, () => {
          aborterHandler?.abort();
          params.tasksActions.deleteTask(taskId);
        });
      };
      const onUploadFileProgress = (progressEvent: { loaded: number; total: number }) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        params.tasksActions.updateProgress(taskId, percentCompleted, false);
      };

      await params.uploadAction(sasToken.uri, file as File, onUploadFileProgress, acceptUploadAborter);
      params.tasksActions.updateTask(taskId, scormTasks.getUploadedSuccessfully(taskId));
    };

    const { id, sasToken } = await getResource();
    params.tasksActions.updateId(oldTaskId, id);
    params.onTaskIdChanged(id);

    try {
      await uploadFile(id, sasToken);

      if (exists) {
        await params.updateFileAction({ id, fileName: file.name, packagePath: sasToken.fileName });
      } else {
        await params.createAction({ id, fileName: file.name, packagePath: sasToken.fileName });
      }
      params.tasksActions.updateTask(id, scormTasks.getProcessing(id));
    } catch (error) {
      const task =
        error.message === "The request was aborted"
          ? scormTasks.getCanceled(id, params.tasksActions.deleteTask)
          : scormTasks.getFailed(id, params.tasksActions.deleteTask);

      params.tasksActions.updateTask(id, task);
    }
  };

  const uploadFilesHandler = async (files: FileLike[]) =>
    Promise.all(
      Array.from(files).map((file: FileLike) => {
        params.tasksActions.addTask(scormTasks.getStarted(file.name, file.name));
        return uploadFileHandler(file.name, file);
      }),
    );

  const onCancelFileUploading = () => {
    aborter?.signal.addEventListener("abort", params.uploadCancelAction);
    aborter?.abort();
  };

  return {
    uploadFilesHandler,
    onCancelFileUploading,
  };
};

export default uploaderService;
